import React from 'react';
import { Check } from 'lucide-react';
import { SHARED_IMG } from './shared';

// These three photos already ship with the /turo-alternative port as .webp,
// so they are reused rather than duplicated from the v0 source.
const OFFERINGS = [
  {
    eyebrow: 'Telematics tracking',
    title: 'Track vehicles on trip.',
    copy: 'Connect your telematics to view every vehicle live on the map and stay in the loop from pickup to return.',
    points: [
      'Real-time GPS location per trip',
      'Route history with every stop',
      'Instant alerts for unusual activity',
    ],
    image: `${SHARED_IMG}/inshur/telematics-tracking.webp`,
    alt: 'Over-the-shoulder view of a person watching a live vehicle-tracking map on their phone with a location pin and route stops',
  },
  {
    eyebrow: 'Roadside assistance',
    title: 'Help on the road, built in.',
    copy: 'Give every renter 24/7 roadside support so a breakdown never becomes your problem.',
    points: [
      'Tow, jump-start, lockout services',
      'Tire change and fuel replacement',
      'Leisure and ride share plans available',
    ],
    image: `${SHARED_IMG}/inshur/roadside-assistance.webp`,
    alt: 'A roadside assistance technician helping a driver next to an SUV parked on the shoulder at golden hour',
  },
  {
    eyebrow: 'Tolls management',
    title: 'Tolls, tracked and billed.',
    copy: "Every toll is automatically captured and passed through to the renter's bill. No more manual lookups or paperwork.",
    points: [
      'Automatic toll capture per trip',
      'Passed through to the renter',
      'Reconciled in one dashboard',
    ],
    image: `${SHARED_IMG}/inshur/tolls-management.webp`,
    alt: 'A car driving through an open-road electronic highway toll gantry on a clear day',
  },
];

const OfferingCard = ({ offering }) => (
  <div className="ilp-offer ilp-card">
    <img src={offering.image} alt={offering.alt} className="ilp-offer-img" />

    <span aria-hidden="true" className="ilp-offer-glyph">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polyline className="ilp-offer-glyph-tr" points="14 4 20 4 20 10" />
        <polyline className="ilp-offer-glyph-bl" points="10 20 4 20 4 14" />
      </svg>
    </span>

    {/* Category label, fades out as the detail panel rises */}
    <div className="ilp-offer-label">
      <span className="ilp-offer-dot" aria-hidden="true" />
      <p>{offering.eyebrow}</p>
    </div>

    {/* Detail panel — slides up over the image on hover */}
    <div className="ilp-offer-detail">
      <div className="ilp-offer-detail-head">
        <span className="ilp-offer-dot" aria-hidden="true" />
        <p>{offering.eyebrow}</p>
      </div>
      <h3>{offering.title}</h3>
      <p className="ilp-offer-copy">{offering.copy}</p>
      <ul className="ilp-offer-points">
        {offering.points.map((p) => (
          <li key={p}>
            <span className="ilp-offer-check">
              <Check size={10} strokeWidth={3} />
            </span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/** Three hover-to-reveal cards for the built-in operational workflows. */
const RoadsideTolls = () => (
  <section className="ilp-roadside">
    <div className="ilp-roadside-panel">
      <div className="ilp-roadside-inner">
        <div className="ilp-roadside-head">
          <p className="ilp-eyebrow">Automated Operational Workflows</p>
          <h2 className="ilp-roadside-title">Roadside, tolls, and telematics, built in to every trip</h2>
          <p className="ilp-roadside-sub">
            Automated roadside assistance, toll reconciliation, and integrated vehicle telematics.
          </p>
        </div>

        <div className="ilp-roadside-grid">
          {OFFERINGS.map((o) => (
            <OfferingCard key={o.eyebrow} offering={o} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default RoadsideTolls;
